import React, { useState, useCallback, useMemo, useEffect } from 'react';
import * as echarts from 'echarts';
import { BaseChart, Retry } from '@components/common';
import { DefaultLayer as Loading } from '@components/common/Retry/Elements';
import { DURATION_BUCKET_COLOR, arddDimension, arddMetric } from '../constants';
import { WidgetContainer } from '../../styled';
import { useExternalState } from '../hooks/useExternalState';
import { useStackedDurationHooks } from '../hooks/useStackedHooks';
import { buildStackedOption, getStackedTimeSeries } from '../ResponseCode/utils';
import {useAvailable} from '../hooks/useAvailable';
import NetworkContext from '../context';

const dimension = arddDimension;
const metric = arddMetric;

const orderBuckets = Object.keys(DURATION_BUCKET_COLOR).reverse();

function StackedDuration() {
  const { enableSSF, enableTimeSlice } = React.useContext(NetworkContext);
  const { interval } = useAvailable(enableSSF, enableTimeSlice)
  const { isLiveMode, enableLoading, keepPreviousData } = useExternalState();

  const [instance, setInstance] = useState<
    echarts.ECharts | null
  >(null);
  const chartInstanceRefCb: any = useCallback((instance) => {
    setInstance(instance);
  }, []);

  const { data, ...status } = useStackedDurationHooks({
    dimension,
    interval,
    metric,
    keepPreviousData,
  });

  const chartData = useMemo(() => {
    if (!data) { return undefined; }
    return getStackedTimeSeries({
      data,
      series: orderBuckets,
      colorMap: DURATION_BUCKET_COLOR,
      groupBy: dimension,
      metric,
      interval,
    });
  }, [data, interval]);

  useEffect(() => {
    if (instance !== null && !status.isLoading) {
      const option = buildStackedOption(chartData, isLiveMode) as any;
      instance.setOption(option, { notMerge: true });
    }
  }, [instance, chartData, isLiveMode, status.isLoading]);

  return (
    <WidgetContainer $isEmpty={status.isEmpty}>
      <Retry
        {...status}
        progress
      >
        {enableLoading && status.isLoading && <Loading progress duration={1} />}
        <BaseChart
          key="network-duration-chart"
          chartId="network-duration-chart"
          chartInstanceRefCb={chartInstanceRefCb}
          style={{ height: '100%', minHeight: 310 }}
        />
      </Retry>
    </WidgetContainer>
  )
}

export default StackedDuration;
